"use client"

import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useEffect as useEffectGA } from 'react'
import NewNavigationBar from '@/components/NewNavigationBar'
import Footer from '@/components/Footer'
import Home from '@/pages/Home'
import NewExcel from '@/pages/NewExcel'
import TodaySalesPage from '@/pages/TodaySales' // Added import for the new page
import AuthCallback from '@/pages/AuthCallback'
import Guide from '@/pages/Guide'
import MyPage from '@/pages/MyPage'
import NoticePage from '@/pages/Notice' // Added import for the new page
import LandingPage from '@/pages/Landing' // Added import for LandingPage
import Dashboard from '@/pages/Dashboard'
import InvitePage from '@/pages/Invite'
import AdminReferralPage from '@/pages/admin/Referral'
import AdminBillingRetryPage from '@/pages/admin/BillingRetry'
import PaymentCallback from '@/pages/payment/callback'
import Deposits from '@/pages/Deposits'
import Inventory from '@/pages/Inventory'
import { LoginModalProvider } from '@/contexts/LoginModalContext'
// import { useEffect, useMemo } from 'react'
import { useEffect, useRef } from 'react'
import { loadPortOneSDK } from '@/lib/payment/portone'
import { ChannelTalk } from '@/lib/channel'
import { useAuth } from '@/hooks/useAuth'
import { AuthProvider } from '@/contexts/AuthContext'
import { SubscriptionProvider } from '@/contexts/SubscriptionContext'
import { Toaster } from 'sonner'
import { initSentry, setSentryUser, clearSentryUser } from '@/lib/sentry'
import SentryErrorBoundary from '@/components/SentryErrorBoundary'
import SentryTest from '@/components/SentryTest'
import SignupBanner from '@/components/SignupBanner' // Added import for SignupBanner
import PremiumTrialExpiryBanner from '@/components/PremiumTrialExpiryBanner' // Added import for PremiumTrialExpiryBanner
import ReferralWelcomeModal from '@/components/ReferralWelcomeModal' // Added import for ReferralWelcomeModal
import { LaunchNoticeGate } from '@/components/LaunchNoticeGate'
import { PgBetaInviteModal } from '@/components/PgBetaInviteModal'
import { PG_BETA_INVITE_USER_IDS } from '@/lib/pgBetaInvite'
import { HelmetProvider } from 'react-helmet-async'

// Sentry 초기화
initSentry()

// SDK 로드 상태 플래그 (중복 로드 방지)
let sdkLoaded = false

// 테스트 유저 ID 목록 - New 접두사가 붙은 컴포넌트가 보여질 사용자 ID 배열
// const TEST_USER_IDS = [
//   "bd873ddc-9c11-4093-a4e6-a2fb74e2cb71",  // 내 gmail
//   // "e099a8b7-378b-41b7-89d5-f0977f23333f",   // 내 카카오
//   "2c27ab66-7f8b-4141-b84a-cdbc1fac48db",   // 형 계정
//   "f2934714-7583-4189-ab39-8133eb1f6917"   // 셀러헬퍼 테스트 계정
// ]

// Router 외부에서 사용할 함수들
const loadSDKs = () => {
  // 이미 로드된 경우 중복 실행 방지
  if (sdkLoaded) return
  sdkLoaded = true
  
  // 포트원 SDK 로드
  loadPortOneSDK().catch(() => {}) // 미리 로드해두기 (캐시 warming)
  
  // 채널톡 스크립트만 로드 (부트는 별도 처리)
  ChannelTalk.load()
}

// 실제 앱 컴포넌트 (Router 내부에서 사용)
function AppContent() {
  const { user, isAuthenticating } = useAuth()
  const isPgBetaTarget = !!user && PG_BETA_INVITE_USER_IDS.has(user.id)
  const location = useLocation()
  const lastSentRef = useRef<string>('')
  const lastLocationHrefRef = useRef<string>('')
  
  // Google Analytics route change tracking (debounced, with referrer & dedupe)
  useEffectGA(() => {
    const gtag = (window as any).gtag
    if (!gtag) return

    // page_path에서 hash는 기본 제외 (필요 페이지에서만 포함)
    const page_path = location.pathname + location.search
    const page_location = window.location.href
    const page_title = document.title

    // SPA referrer 보강: 이전 location.href를 page_referrer로 전달
    const page_referrer = lastLocationHrefRef.current || document.referrer || ''

    // 동일 URL 연속 발화 방지
    const key = page_location
    if (lastSentRef.current === key) return

    const timer = setTimeout(() => {
      gtag('event', 'page_view', {
        page_title,
        page_location,
        page_path,
        page_referrer,
      })
      // 업데이트: 마지막 전송 URL / 마지막 location 저장
      lastSentRef.current = key
      lastLocationHrefRef.current = page_location
    }, 300)

    return () => clearTimeout(timer)
  }, [location.pathname, location.search])
  
  // 현재 유저가 테스트 유저인지 확인
  // const isTestUser = useMemo(() => {
  //   if (!user) return false
  //   return TEST_USER_IDS.includes(user.id)
  // }, [user])
  
  // SDK 미리 로드 (스크립트만)
  useEffect(() => {
    loadSDKs()
  }, [])
  
  // 유저 상태에 따른 채널톡 초기화 처리 + GA user_id/user_properties
  useEffect(() => {
    if (user) {
      // 회원인 경우: 기존 상태 정리 후 회원 정보로 부트
      ChannelTalk.shutdown()
      ChannelTalk.bootWithMember(user.id, {
        name: user.user_metadata?.full_name || '회원'
      })
      
      // Sentry에 사용자 정보 설정
      setSentryUser({
        id: user.id,
        email: user.email,
        name: user.user_metadata?.full_name
      })

      // GA user 연결
      const gtag = (window as any).gtag
      if (gtag) {
        gtag('set', { user_id: user.id })
        gtag('set', 'user_properties', {
          plan: user.user_metadata?.plan ?? 'free',
          is_logged_in: 'true',
        })
      }
    } else {
      // 비회원인 경우: 기존 상태 정리 후 기본 부트
      ChannelTalk.shutdown()
      ChannelTalk.boot()
      
      // Sentry 사용자 정보 초기화
      clearSentryUser()

      // GA user 해제
      const gtag = (window as any).gtag
      if (gtag) {
        gtag('set', { user_id: null })
        gtag('set', 'user_properties', { is_logged_in: 'false', plan: 'guest' })
      }
    }
  }, [user])

  // LandingPage가 루트 경로('/')일 때 NavBar와 SignupBanner를 숨기도록 조건 변경
  const isLandingPage = location.pathname === '/'
  const showNavBar = !isLandingPage
  const showSignupBanner = !user && !isLandingPage
  

  return (
    <LoginModalProvider>
      <div className="min-h-screen bg-gray-100 flex flex-col">
        {showNavBar && <NewNavigationBar />}
        <main className="flex-grow">
          <Routes>
            <Route
              path="/"
              element={user && !isAuthenticating ? <Navigate to="/order" replace /> : <LandingPage />}
            />
            {/* <Route path="/excel" element={isTestUser ? <NewExcel /> : <Excel />} /> */}
            <Route path="/order" element={<Home />} />
            <Route path="/deposits" element={<Deposits />} />
            <Route path="/inventory" element={<Inventory />} />
            <Route path="/excel" element={<NewExcel />} /> {/* NewExcel을 /excel 경로로 이동 */}
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/guide" element={<Guide />} />
            <Route path="/mypage" element={<MyPage />} />
            <Route path="/payment/callback" element={<PaymentCallback />} />
            <Route path="/sentry-test" element={<SentryTest />} />
            <Route path="/today-sales" element={<TodaySalesPage />} /> {/* Added route for TodaySalesPage */}
            <Route path="/notice" element={<NoticePage />} /> {/* Added route for NoticePage */}
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/invite" element={<InvitePage />} /> {/* 초대장 페이지 */}
            <Route path="/admin/referral" element={<AdminReferralPage />} /> {/* 어드민 - 초대장 관리 */}
            <Route path="/admin/billing-retry" element={<AdminBillingRetryPage />} /> {/* 어드민 - 결제 실패 재시도 */}
            {/* /landing 경로는 이제 사용하지 않으므로 주석 처리 또는 삭제 가능 */}
            {/* <Route path="/landing" element={<LandingPage />} /> */}
            
            {/* ⚡️ 정의되지 않은 경로는 모두 홈으로 리디렉션 */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        <Footer />
        <Toaster position="bottom-center" />
        {user && <PremiumTrialExpiryBanner />} {/* Conditionally render PremiumTrialExpiryBanner for logged-in users */}
        {showSignupBanner && <SignupBanner />} {/* Conditionally render SignupBanner */}
        {user && <ReferralWelcomeModal />} {/* 초대 코드로 가입한 직후 표시 */}
        {/* 카드결제 베타 초대 대상에게는 아래 공지 대신 초대 모달만(docs/plan-pg-beta-invite.md) */}
        {/* 연말 초대 공지 또는 출시 공지(입금매칭·재고관리) 중 하나만 — 우선순위는 LaunchNoticeGate.
            재고관리 플래그 게이트는 게이트 안 렌더 레벨에 그대로 있다(codexloop PR7-3 R1 수용). */}
        {user && !isPgBetaTarget && <LaunchNoticeGate />}
        {isPgBetaTarget && <PgBetaInviteModal />}
      </div>
    </LoginModalProvider>
  )
}

// 최상위 앱 컴포넌트 (Router를 포함)
export default function App() {
  return (
    <HelmetProvider>
      <Router>
        <SentryErrorBoundary>
          <AuthProvider>
            <SubscriptionProvider>
              <AppContent />
            </SubscriptionProvider>
          </AuthProvider>
        </SentryErrorBoundary>
      </Router>
    </HelmetProvider>
  )
}
